import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import AddButton from 'components/v2/buttons/AddButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import CargomaticInput from 'components/v2/input';
import Pagination from 'components/v2/PaginationV2';
import CargomaticSelect from 'components/v2/select';
import ToolTip from 'components/v2/Tooltip';
import { Edit, LoaderCircle, MoreHorizontal, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';
import { unescapeHTML } from 'underscore.string';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  createException,
  useGetBridgeReasonCodes,
  useGetExceptions,
} from 'utils/BridgeCodeAPIUtils';

const DOCUMENTS_PER_PAGE = 5;

const isApproveDisabled = (exception, reasonCodes) => {
  const hasReasonCode = !reasonCodes?.length || exception.reason_code;
  const hasBridgeCode = !!exception?.bridge_code;
  const hasShipmentLeg = !!exception?.shipment_leg;

  return !(hasReasonCode && hasBridgeCode && hasShipmentLeg);
};

const EditDropdown = ({ exception, disabled = false, onSelect }) => {
  const { t } = useTranslation();
  const exception_id = exception._id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          data-testid={`${exception_id}-Dropdown-d41900dd`}
          aria-label={t('MORE_OPTIONS')}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          data-testid={`${exception_id}-Dropdown-button-d41909dd`}
          onSelect={() => onSelect(exception, 'edit')}
        >
          <Edit className="h-4 w-4 mr-2" />
          {t('EDIT')}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${exception_id}-Dropdown-button-d46900dd`}
          onSelect={() => onSelect(exception, 'delete')}
          className="text-red-600"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {t('DELETE')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ExceptionActionConfirmation = ({
  exception,
  action,
  disableApprove,
  handleApprove,
  handleCancel,
}) => {
  const { t } = useTranslation();
  const exception_id = exception._id;
  const isEdit = action === 'edit';
  const approveTooltipLabel = isEdit
    ? t('SAVE_EXCEPTION')
    : t('DELETE_EXCEPTION');
  return (
    <div className="flex gap-[5px]">
      <ToolTip
        dataTestId={`${exception_id}-CargomaticApproveTooltip-74bc9003`}
        id={`${exception_id}_approve_button`}
        position="left"
        bodyParams={{
          dataTestId: `${exception_id}-ApproveButton-c6f695g8`,
          onClick: () => {
            handleApprove(exception_id);
          },
          disabled: disableApprove,
        }}
        body={({ dataTestId, onClick, disabled }) =>
          isEdit ? (
            <ApproveButton
              dataTestId={dataTestId}
              onClick={onClick}
              disabled={disabled}
            />
          ) : (
            <DeleteButton
              dataTestId={dataTestId}
              onClick={onClick}
              disabled={disabled}
            />
          )
        }
      >
        {approveTooltipLabel}
      </ToolTip>
      <ToolTip
        dataTestId={`${exception_id}-CargomaticDeclineTooltip-09f91576`}
        id={`${exception_id}_decline_button`}
        position="left"
        bodyParams={{
          dataTestId: `${exception_id}-DeclineButton-09f91576`,
          onClick: () => {
            handleCancel(exception_id);
          },
        }}
        body={({ dataTestId, onClick }) => (
          <DeclineButton onClick={onClick} dataTestId={dataTestId} />
        )}
      >
        {t('CANCEL')}
      </ToolTip>
    </div>
  );
};

const ExceptionActionButton = ({
  exception,
  hasEditAccess,
  onDropdownSelect,
}) => {
  const { t } = useTranslation();
  const exception_id = exception._id;
  if (!featureEnabled(CONFIGURED_FEATURES.BULK_MARKET_EXCEPTIONS)) return <></>;
  if (!hasEditAccess) {
    return (
      <ToolTip
        dataTestId={`${exception_id}CargomaticTooltip-74bc9003`}
        id={`${exception_id}document_list`}
        position="left"
        bodyParams={{
          exception_id,
          disabled: !hasEditAccess,
          onSelect: onDropdownSelect,
          exception,
        }}
        body={EditDropdown}
      >
        {t('BRIDGE_ROLE_INFO_TOOLTIP')}
      </ToolTip>
    );
  }
  return (
    <EditDropdown
      exception_id={exception_id}
      exception={exception}
      onSelect={onDropdownSelect}
    />
  );
};

const CreateRow = ({
  legsItemList,
  legsToBridgesMap,
  bridgesToReasonCodesMap,
  handleAddException,
}) => {
  const { t } = useTranslation();
  const [shipmentLeg, setShipmentLeg] = useState({});
  const [bridgeCode, setBridgeCode] = useState({});
  const [reasonCode, setReasonCode] = useState({});
  const [note, setNote] = useState('');

  const bridgeItemList = useMemo(() => {
    return legsToBridgesMap?.[shipmentLeg?._id] || [];
  }, [shipmentLeg]);

  const reasonCodesItemList = useMemo(() => {
    return bridgesToReasonCodesMap?.[bridgeCode?._id] || [];
  }, [bridgeCode]);

  const isAddDisabled = useMemo(() => {
    const hasReasonCode = !reasonCodesItemList?.length || reasonCode?._id;
    const hasBridgeCode = !!bridgeCode?._id;
    const hasShipmentLeg = !!shipmentLeg?._id;

    return !(hasReasonCode && hasBridgeCode && hasShipmentLeg);
  }, [reasonCode, bridgeCode, shipmentLeg]);

  const handleLegSelect = (item) => {
    setShipmentLeg(item);
    setBridgeCode({});
    setReasonCode({});
  };

  const handleBridgeSelect = (item) => {
    setBridgeCode(item);
    setReasonCode({});
  };

  const handleReasonSelect = (item) => {
    setReasonCode(item);
  };

  return (
    <TableRow>
      <TableCell>
        <CargomaticSelect
          dataTestId="CargomaticSelect-92dc5d28"
          showCaption
          hideLabel
          items={legsItemList}
          selected={shipmentLeg?.value || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={handleLegSelect}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_LEG')}
        />
      </TableCell>
      <TableCell>
        <CargomaticSelect
          dataTestId="CargomaticSelect-6c1cb9a8"
          showCaption
          hideLabel
          items={bridgeItemList}
          selected={bridgeCode?.value || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={handleBridgeSelect}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="value"
          caption={t('SELECT_BRIDGE')}
        />
      </TableCell>
      <TableCell>
        <CargomaticSelect
          dataTestId="CargomaticSelect-1d50d4d0"
          showCaption
          hideLabel
          items={reasonCodesItemList}
          selected={reasonCode?.value || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={handleReasonSelect}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="value"
          caption={t('SELECT_REASON_CODE')}
        />
      </TableCell>
      <TableCell>
        <CargomaticInput
          dataTestId="CargomaticInput-62f5540f"
          hideLabel
          type="text"
          name="template-name"
          className="w-full"
          placeholder={t('NOTES')}
          value={note}
          onChange={(value) => {
            setNote(value);
          }}
          disabled={isAddDisabled}
        />
      </TableCell>
      <TableCell>
        <AddButton
          dataTestId="AddButton-0ed24a4d"
          onClick={() => {
            handleAddException({ shipmentLeg, bridgeCode, reasonCode, note });
            setShipmentLeg();
            setBridgeCode({});
            setReasonCode({});
            setNote('');
          }}
          disabled={isAddDisabled}
        />
      </TableCell>
    </TableRow>
  );
};

const EditRow = ({
  exception,
  legsItemList,
  legsToBridgesMap,
  bridgesToReasonCodesMap,
  handleApproveEdit,
  handleCancelEdit,
}) => {
  const { t } = useTranslation();
  const [exceptionData, setExceptionData] = useState(exception);
  const exception_id = exceptionData._id;
  const leg = exceptionData?.shipment_leg || '';
  const legId =
    exceptionData?.shipment_leg_id ||
    legsItemList?.find((item) => item.value == leg)?._id ||
    '';
  const bridgeCodes = legsToBridgesMap?.[legId] || [];
  const bridgeCode = exceptionData?.bridge_code || '';
  const bridgeCodeId =
    exceptionData.bridge_code_id ||
    bridgeCodes.find((item) => item.value == bridgeCode)?._id ||
    '';
  const reasonCodes = bridgesToReasonCodesMap?.[bridgeCodeId] || [];

  const disableApprove = isApproveDisabled(exceptionData, reasonCodes);

  const handleEditException = (field, value, value_id) => {
    let newException = {
      [field]: value,
      [`${field}_id`]: value_id,
    };

    if (field === 'shipment_leg') {
      Object.assign(newException, {
        bridge_code: '',
        bridge_code_id: '',
        reason_code: '',
        reason_code_id: '',
      });
    } else if (field === 'bridge_code') {
      Object.assign(newException, {
        reason_code: '',
        reason_code_id: '',
      });
    }
    setExceptionData((prev) => {
      return {
        ...prev,
        ...newException,
      };
    });
  };
  return (
    <TableRow key={exception._id}>
      <TableCell className="break-words">
        <CargomaticSelect
          dataTestId={`CargomaticSelect-92dc5d28-${exception_id}`}
          showCaption
          hideLabel
          items={legsItemList}
          selected={exceptionData.shipment_leg || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={(selectedItem) => {
            handleEditException(
              'shipment_leg',
              selectedItem.value,
              selectedItem._id,
            );
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_LEG')}
        />
      </TableCell>
      <TableCell className="break-words">
        <CargomaticSelect
          dataTestId="CargomaticSelect-6c1cb9a8"
          showCaption
          hideLabel
          items={bridgeCodes}
          selected={exceptionData.bridge_code || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={(selectedItem) => {
            handleEditException(
              'bridge_code',
              selectedItem.value,
              selectedItem._id,
            );
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="value"
          caption={t('SELECT_BRIDGE')}
        />
      </TableCell>
      <TableCell className="break-words">
        <CargomaticSelect
          dataTestId="CargomaticSelect-1d50d4d0"
          showCaption
          hideLabel
          items={reasonCodes}
          selected={exceptionData.reason_code || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={(selectedItem) => {
            handleEditException(
              'reason_code',
              selectedItem.value,
              selectedItem._id,
            );
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="value"
          caption={t('SELECT_REASON_CODE')}
        />
      </TableCell>
      <TableCell className="break-words">
        <CargomaticInput
          dataTestId="CargomaticInput-62f5540f"
          hideLabel
          type="text"
          name="template-name"
          className="w-full"
          placeholder={t('NOTES')}
          value={exceptionData.reason_description}
          onChange={(value) => {
            handleEditException('reason_description', value);
          }}
          disabled={disableApprove}
        />
      </TableCell>
      <TableCell className="break-words">
        <ExceptionActionConfirmation
          exception={exception}
          action="edit"
          handleCancel={handleCancelEdit}
          handleApprove={() => handleApproveEdit(exceptionData)}
          disableApprove={disableApprove}
          key={`${exception._id}-exception-action`}
        />
      </TableCell>
    </TableRow>
  );
};

const ReadOnlyRow = ({
  action,
  exception,
  hasEditAccess,
  handleApproveDelete,
  handleCancelDelete,
  onDropdownSelect,
}) => {
  const { t } = useTranslation();
  const LEG_UPPERCASE = exception.shipment_leg?.toUpperCase() || '';
  return (
    <TableRow key={exception._id}>
      <TableCell className="break-words">
        {unescapeHTML(t(LEG_UPPERCASE))}
      </TableCell>
      <TableCell className="break-words">
        {unescapeHTML(exception.bridge_code)}
      </TableCell>
      <TableCell className="break-words">
        {unescapeHTML(exception.reason_code)}
      </TableCell>
      <TableCell className="break-words">
        {unescapeHTML(exception.reason_description)}
      </TableCell>
      <TableCell className="break-words">
        {action === 'delete' ? (
          <ExceptionActionConfirmation
            exception={exception}
            action="delete"
            key={`${exception._id}-exception-action`}
            handleApprove={handleApproveDelete}
            handleCancel={handleCancelDelete}
          />
        ) : (
          <ExceptionActionButton
            exception={exception}
            hasEditAccess={hasEditAccess}
            onDropdownSelect={onDropdownSelect}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const ExceptionRow = ({
  legsToBridgesMap,
  legsItemList,
  bridgesToReasonCodesMap,
  handleApproveDelete,
  handleUpdateException,
  handleCancelEdit,
  handleCancelDelete,
  onDropdownSelect,
  exception,
  hasEditAccess,
  editingIds,
}) => {
  const exception_id = exception._id;
  const action = editingIds[exception_id];
  switch (action) {
    case 'edit':
      return (
        <EditRow
          legsToBridgesMap={legsToBridgesMap}
          legsItemList={legsItemList}
          bridgesToReasonCodesMap={bridgesToReasonCodesMap}
          handleApproveEdit={handleUpdateException}
          handleCancelEdit={handleCancelEdit}
          exception={exception}
          key={`edit_row_${exception_id}`}
        />
      );
    case 'delete':
      return (
        <ReadOnlyRow
          handleApproveDelete={handleApproveDelete}
          handleCancelDelete={handleCancelDelete}
          action={action}
          hasEditAccess={hasEditAccess}
          exception={exception}
          onDropdownSelect={onDropdownSelect}
          key={`readonly_row_delete`}
        />
      );
    default:
      return (
        <ReadOnlyRow
          action={action}
          hasEditAccess={hasEditAccess}
          onDropdownSelect={onDropdownSelect}
          exception={exception}
          key={`readonly_row_read`}
        />
      );
  }
};

function ShipmentBridges({
  shipmentId,
  isPaginated = true,
  showHeader: _showHeader = true,
}) {
  const { t } = useTranslation();
  const { data: defaultBridgeCodes, isLoading: defaultBridgeCodesLoading } =
    useGetBridgeReasonCodes({
      onError: () => {
        showToast(t('SHIPPER_BRIDGE_REASON_CODES_FETCH_FAILED'), '', 'error');
      },
    });

  const exceptionParams = useMemo(
    () => ({ shipment_id: shipmentId, is_active: true }),
    [shipmentId],
  );
  const {
    data: exceptionsData,
    isLoading: exceptionsLoading,
    refetch: refetchExceptions,
  } = useGetExceptions(exceptionParams, {
    onError: () => {
      showToast(t('SHIPPER_EXCEPTION_FETCH_FAILED'), '', 'error');
    },
  });

  const [page, setPage] = useState(1);
  // Map of exception_id : 'action'
  const [editingIds, setEditingIds] = useState({});

  const totalExceptions = useMemo(
    () => exceptionsData?.length || 0,
    [exceptionsData],
  );

  const pageCount = useMemo(
    () => Math.ceil(totalExceptions / DOCUMENTS_PER_PAGE),
    [totalExceptions],
  );

  const paginatedExceptions = useMemo(() => {
    if (!isPaginated) {
      return exceptionsData;
    }
    const startIndex = (page - 1) * DOCUMENTS_PER_PAGE;
    const endIndex = startIndex + DOCUMENTS_PER_PAGE;
    return exceptionsData?.slice(startIndex, endIndex) || [];
  }, [exceptionsData, page]);

  useEffect(() => {
    refreshPagination();
  }, [totalExceptions]);

  const refreshPagination = () => {
    if (page > 1 && pageCount < page) {
      setPage(page - 1);
    }
  };

  const legs = useMemo(() => {
    const drayageCategory =
      defaultBridgeCodes?.find((item) => item.lineOfBusiness === 'drayage') ||
      [];
    const shipmentLegs =
      drayageCategory?.categories?.filter(
        (item) => item.categoryType === 'market_legs',
      ) || [];
    return shipmentLegs;
  }, [defaultBridgeCodes]);

  const legsToBridgesMap = useMemo(() => {
    const map = {};

    legs?.forEach((leg) => {
      const bridges =
        leg?.subCategories?.map((bridge) => ({
          _id: bridge._id,
          value: bridge.reasonType,
        })) || [];
      map[leg._id] = bridges;
    });

    return map;
  }, [legs]);

  const bridgesToReasonCodesMap = useMemo(() => {
    const map = {};

    legs.forEach((leg) => {
      leg?.subCategories?.forEach((bridge) => {
        const bridgeId = bridge?._id;
        map[bridgeId] = bridge?.reasonCodes?.map((reasonCode) => ({
          _id: reasonCode?._id,
          value: reasonCode.description,
        }));
      });
    });

    return map;
  }, [legs]);

  const legsItemList = useMemo(() => {
    return (
      legs?.map((category) => {
        const CATEGORY_UPPERCASE = category?.name?.toUpperCase() || '';
        return {
          _id: category._id,
          value: category.name,
          localizationSlug: CATEGORY_UPPERCASE,
        };
      }) || []
    );
  }, [legs]);

  const hasEditAccess = useMemo(() => SessionStore.isBridgeCodeManager());

  const handleClearForm = (exceptionId) => {
    if (exceptionId) {
      return setEditingIds((prev) => {
        const updated = { ...prev };
        delete updated[exceptionId];
        return updated;
      });
    }
    return setEditingIds({});
  };

  const handleAddException = async ({
    shipmentLeg,
    bridgeCode,
    reasonCode,
    note = '',
  }) => {
    const shipment_leg = shipmentLeg.value || '';
    const bridge_code = bridgeCode.value || '';
    const reason_code = reasonCode.value || '';
    const trimmedNote = note?.trim() || '';
    const createBody = {
      shipment_id: shipmentId,
      shipment_leg,
      bridge_code,
      reason_code,
      reason_description: trimmedNote,
    };

    const body = {
      create: [createBody],
    };

    try {
      const response = await createException(body);
      if (response.executedOperations.length === 1) {
        showToast(t('MARKET_EXCEPTION_CREATION_SUCCESS'));
      } else {
        showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
      }
      refetchExceptions();
      // handleClearForm();
    } catch {
      showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
    }
  };

  const handleUpdateException = async (exceptionData) => {
    const { shipment_leg, bridge_code, reason_code, reason_description } =
      exceptionData;
    const updateBody = {
      shipment_leg,
      bridge_code,
      reason_code,
      reason_description,
      exception_id: exceptionData._id,
      shipment_id: shipmentId,
    };
    const body = {
      update: [updateBody],
    };
    try {
      const response = await createException(body);
      if (response.executedOperations.length >= 1) {
        showToast(t('MARKET_EXCEPTION_UPDATION_SUCCESS'));
      } else {
        showToast(t('MARKET_EXCEPTION_UPDATION_FAIL'), '', 'error');
      }
      refetchExceptions();
      handleClearForm(exceptionData._id);
    } catch {
      showToast(t('MARKET_EXCEPTION_UPDATION_FAIL'), '', 'error');
    }
  };

  const handleDeleteException = async (exception_id) => {
    const deleteBody = {
      exception_id,
      shipment_id: shipmentId,
    };
    const body = {
      delete: [deleteBody],
    };
    try {
      const response = await createException(body);
      if (response.executedOperations.length === 1) {
        showToast(t('MARKET_EXCEPTION_DELETION_SUCCESS'));
      } else {
        showToast(t('MARKET_EXCEPTION_DELETION_FAIL'), '', 'error');
      }
      refetchExceptions();
      handleClearForm(exception_id);
    } catch {
      showToast(t('MARKET_EXCEPTION_DELETION_FAIL'), '', 'error');
    }
  };

  const handleCancelEdit = (exception_id) => {
    setEditingIds((prev) => {
      const updated = { ...prev };
      delete updated[exception_id];
      return updated;
    });
  };

  const handleCancelDelete = (exception_id) => {
    setEditingIds((prev) => {
      const updated = { ...prev };
      delete updated[exception_id];
      return updated;
    });
  };

  const handleApproveDelete = (exception_id) => {
    handleDeleteException(exception_id);
  };

  const onDropdownSelect = (exception, action) => {
    setEditingIds((prev) => ({
      ...prev,
      [exception._id]: action,
    }));
  };

  if (defaultBridgeCodesLoading || exceptionsLoading) {
    return (
      <LoaderCircle
        className="w-4 h-4 animate-spin ml-2"
        data-testid="LoaderCircle-f4ff494b"
      />
    );
  }

  return (
    <CargomaticCollapsibleCard
      id="shipment-bridges"
      dataTestId="CargomaticCard-f4ff494b"
      header={() => <span>{t('SHIPMENT_BRIDGE_AND_EVENT_CODES')}</span>}
    >
      <div className="overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[22.5%]" data-testid="TableHead-f4ff494b">
                {t('SHIPMENT_LEG')}
              </TableHead>
              <TableHead className="w-[22.5%]" data-testid="TableHead-f4ff494t">
                {t('BRIDGE')}
              </TableHead>
              <TableHead className="w-[22.5%]" data-testid="TableHead-f4ff494u">
                {t('BRIDGE_REASON_CODE')}
              </TableHead>
              <TableHead
                className="min-w-[22.5%]"
                data-testid="TableHead-f4ff494r"
              >
                {t('NOTES')}
              </TableHead>
              <TableHead className="w-[10%]" data-testid="TableHead-f4ff494i">
                {t('ACTION')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <CreateRow
              legsToBridgesMap={legsToBridgesMap}
              legsItemList={legsItemList}
              bridgesToReasonCodesMap={bridgesToReasonCodesMap}
              handleAddException={handleAddException}
              key="create-row"
            />
            {paginatedExceptions?.map((exception, index) => (
              <ExceptionRow
                legsToBridgesMap={legsToBridgesMap}
                legsItemList={legsItemList}
                bridgesToReasonCodesMap={bridgesToReasonCodesMap}
                handleApproveDelete={handleApproveDelete}
                handleUpdateException={handleUpdateException}
                handleCancelEdit={handleCancelEdit}
                handleCancelDelete={handleCancelDelete}
                onDropdownSelect={onDropdownSelect}
                exception={exception}
                hasEditAccess={hasEditAccess}
                editingIds={editingIds}
                key={`exception_row_${index}`}
              />
            ))}
          </TableBody>
        </Table>
        {isPaginated && (
          <Pagination
            className="border-t-0"
            perPage={DOCUMENTS_PER_PAGE}
            page={page}
            pages={pageCount}
            total={totalExceptions}
            showCount
            onPageClick={(page) => {
              setPage(page);
              handleClearForm();
            }}
          />
        )}
      </div>
    </CargomaticCollapsibleCard>
  );
}

ShipmentBridges.propTypes = {
  shipmentId: PropTypes.string.isRequired,
};

ShipmentBridges.defaultProps = { shipmentId: '' };

export default ShipmentBridges;
